import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {  CheckCircle, XCircle, Check } from "lucide-react";
import { validateUserForm, getFieldError } from "@/lib/validation";
import { getRoleDisplayName } from "@/lib/authUtils";

// Los roles ahora se reciben desde la API mediante props

/**
 * Interfaz para los datos del formulario
 */
export interface IUserFormData {
  nombres: string;
  apellidos: string;
  rut: string;
  email: string;
  password: string;
  roles: string[];
}

/**
 * Props del componente UserForm
 */
interface UserFormProps {
  onSubmit: (data: IUserFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<IUserFormData>;
  mode?: "create" | "edit";
  isEditing?: boolean; // Nueva prop para comportamiento especial en edición
  isPublicRegistration?: boolean; // Nueva prop para registro público
  availableRoles?: string[];
}

/**
 * Componente de formulario para crear y editar usuarios
 *
 * Incluye validación en tiempo real, selección de roles
 * y manejo de estados de carga y error.
 * Optimizado para dispositivos móviles con campos táctiles.
 */
const UserForm: React.FC<UserFormProps> = ({
  onSubmit,
  isLoading = false,
  initialData = {},
  mode = "create",
  isEditing = false,
  isPublicRegistration = false,
  availableRoles = [],
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState<IUserFormData>({
    nombres: initialData.nombres || "",
    apellidos: initialData.apellidos || "",
    rut: initialData.rut || "",
    email: initialData.email || "",
    password: initialData.password || "",
    roles: initialData.roles || (isPublicRegistration ? ["Apoderado"] : []),
  });

  // Estados de validación
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Maneja el cambio en campos de texto
   */
  const handleInputChange = (field: keyof IUserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Marcar como tocado y validar
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Validar campo específico
    const error = getFieldError(field, value, true);
    setErrors((prev) => ({
      ...prev,
      [field]: error || "",
    }));
  };

  /**
   * Maneja el cambio en la selección de roles
   */
  const handleRoleChange = (role: string, checked: boolean) => {
    const newRoles = checked
      ? [...formData.roles, role]
      : formData.roles.filter((r) => r !== role);

    setFormData((prev) => ({ ...prev, roles: newRoles }));
    setTouched((prev) => ({ ...prev, roles: true }));

    // Validar roles
    const error =
      newRoles.length === 0 ? "Debe seleccionar al menos un rol" : "";
    setErrors((prev) => ({
      ...prev,
      roles: error,
    }));
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    const allFields = Object.keys(formData) as (keyof IUserFormData)[];
    const newTouched = allFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(newTouched);

    // Validar todo el formulario
    const validationResult = validateUserForm(formData);
    setErrors(validationResult);

    // Si no hay errores, enviar el formulario
    if (Object.keys(validationResult).length === 0) {
      onSubmit(formData);
    }
  };

  /**
   * Maneja el evento blur de los campos
   */
  const handleBlur = (field: keyof IUserFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  /**
   * Obtiene el estado visual del campo
   */
  const getFieldState = (field: keyof IUserFormData) => {
    if (!touched[field]) return "default";
    return errors[field] ? "error" : "success";
  };

  /**
   * Renderiza el icono del campo según su estado
   */
  const renderFieldIcon = (field: keyof IUserFormData) => {
    const state = getFieldState(field);

    if (state === "error") {
      return <XCircle className="h-4 w-4 text-destructive" />;
    }
    if (state === "success") {
      return <CheckCircle className="h-4 w-4 text-primary" />;
    }
    return null;
  };

  /**
   * Renderiza un campo de entrada con validación
   */
  const renderInputField = (
    field: keyof IUserFormData,
    label: string,
    type: string = "text",
    placeholder?: string
  ) => {
    const state = getFieldState(field);
    const hasError = errors[field] && touched[field];
    const isDisabled = isLoading;

    return (
      <div className="space-y-2">
        <Label htmlFor={field} className="text-sm font-medium text-foreground">
          {label}
        </Label>
        <div className="relative">
          <Input
            id={field}
            type={type}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            placeholder={placeholder}
            disabled={isDisabled}
            className={`h-12 text-base pr-10 ${
              state === "error"
                ? "border-destructive focus:border-destructive focus:ring-destructive"
                : state === "success"
                ? "border-primary focus:border-primary focus:ring-primary"
                : isDisabled
                ? "bg-muted cursor-not-allowed"
                : ""
            }`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {renderFieldIcon(field)}
          </div>
        </div>
        {hasError && <p className="text-sm text-destructive">{hasError}</p>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nombres y Apellidos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderInputField("nombres", "Nombres", "text", "Ej: Juan Carlos")}
        {renderInputField(
          "apellidos",
          "Apellidos",
          "text",
          "Ej: González Silva"
        )}
      </div>

      {/* RUT y Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderInputField("rut", "RUT", "text", "Ej: 12.345.678-9")}
        {renderInputField("email", "Email", "email", "usuario@ejemplo.com")}
      </div>

      {/* Contraseña */}
      {!isEditing &&
        renderInputField(
          "password",
          "Contraseña",
          "password",
          "Mínimo 6 caracteres"
        )}

      {/* Contraseña opcional en modo edición */}
      {isEditing && (
        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="text-sm font-medium text-foreground"
          >
            Contraseña (opcional)
          </Label>
          <div className="relative">
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password")}
              placeholder="Dejar vacío para mantener la actual"
              disabled={isLoading}
              className="h-12 text-base pr-10"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Solo completa si deseas cambiar la contraseña actual
          </p>
        </div>
      )}

      {/* Roles - Solo mostrar si no es registro público */}
      {!isPublicRegistration && (
        <div className="space-y-3">
          <Label className="text-sm font-medium text-foreground">Roles *</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {availableRoles.map((role) => (
              <div
                key={role}
                className="flex items-center space-x-3 p-3 bg-muted rounded-lg hover:bg-accent transition-colors"
              >
                <button
                  type="button"
                  onClick={() =>
                    handleRoleChange(role, !formData.roles.includes(role))
                  }
                  disabled={isLoading}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    formData.roles.includes(role)
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-muted-foreground hover:border-primary"
                  }`}
                >
                  {formData.roles.includes(role) && (
                    <Check className="h-3 w-3" />
                  )}
                </button>
                <Label
                  className="text-sm font-normal cursor-pointer text-foreground"
                  onClick={() =>
                    handleRoleChange(role, !formData.roles.includes(role))
                  }
                >
                  {getRoleDisplayName(role)}
                </Label>
              </div>
            ))}
          </div>
          {errors.roles && (
            <p className="text-sm text-destructive">{errors.roles}</p>
          )}
        </div>
      )}

      {/* Información de contraseña */}
      {!isEditing && (
        <div className="p-4 bg-muted rounded-lg border border-border">
          <p className="text-sm text-muted-foreground font-medium">
            <strong>Requisitos de contraseña:</strong>
          </p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1">
            <li>• Mínimo 6 caracteres</li>
            <li>• Al menos 1 letra</li>
            <li>• Al menos 1 número</li>
            <li>• Sin espacios</li>
          </ul>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="submit"
          disabled={isLoading || Object.keys(errors).some((key) => errors[key])}
          className="min-w-[140px] h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2" />
              {isPublicRegistration
                ? "Registrando..."
                : mode === "create"
                ? "Creando..."
                : "Guardando..."}
            </>
          ) : isPublicRegistration ? (
            "Crear Cuenta"
          ) : mode === "create" ? (
            "Crear Usuario"
          ) : (
            "Guardar Cambios"
          )}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
